import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { DIRECTION } from 'components/constants';
import i18n from 'i18n';
import { downloadBalancesDocumentByIdRequest, getBalancesDocumentsRequest } from 'services/requestAgent';
import { convertDate, convertToFullDate } from 'services/utils';

class BalancesStore {
  isInitialized = false;
  isLoading = false;
  isLoadingBalancesDocuments = false;
  error = null;
  balancesDocumentsList = [];
  totalElements = null;
  sortColumn = {
    sort_column: 'created_at',
    sort_direction: DIRECTION.DESC
  };
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };
  filters = {
    date: null
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetBalancesStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.error = null;
    this.balancesDocumentsList = [];
    this.sortColumn = {
      sort_column: 'created_at',
      sort_direction: DIRECTION.DESC
    };
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };
    this.filters = {
      date: null
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsLoadingBalancesDocuments = (status) => {
    this.isLoadingBalancesDocuments = status;
    this.error = null;
  };

  setPageNumber = (page) => {
    this.pagination.page = page;
  };

  setPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.sortColumn.sort_column = sortData.sortBy;
    this.sortColumn.sort_direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };
    const pagination = { ...this.pagination };

    // Mapping URL filter parameters to corresponding properties in the 'filters' object
    const filterParamsMapping = {
      date: 'date'
    };

    // Iterating over each URL parameter and assigning its values to 'filters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Convert `from` and `to` if they exist
        if (param === 'date') {
          value = convertToFullDate(value);
        }

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Assigning pagination parameters from the URL
    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    // Assigning sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    // Updating the state of filters and pagination
    this.filters = filters;
    this.pagination = pagination;
  };

  prepareFiltersParams = () => {
    const params = { ...this.filters };

    if (this.filters.date) {
      params.date = convertDate(this.filters.date);
    } else {
      delete params.date;
    }

    return params;
  };

  getBalancesDocumentsList = async () => {
    this.setIsLoading(true);
    try {
      const filtersParams = this.prepareFiltersParams();

      const res = await getBalancesDocumentsRequest(
        this.pagination,
        this.sortColumn.sort_column,
        this.sortColumn.sort_direction,
        filtersParams
      );

      runInAction(() => {
        this.isInitialized = true;
        this.balancesDocumentsList = res.content;
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  downloadBalancesDocumentById = async (documentId) => {
    this.setIsLoadingBalancesDocuments(true);

    const toastPromise = toast.promise(downloadBalancesDocumentByIdRequest(documentId), {
      pending: i18n.getMessage('balancesStore.downloadBalancesDocumentById.toastPromise.pending'),
      success: i18n.getMessage('balancesStore.downloadBalancesDocumentById.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoadingBalancesDocuments(false);
    }
  };
}

export default new BalancesStore();
